import React from "react";
import { Text } from "@chakra-ui/react";

import { availabilityLabel } from "../../helpers/availability";

const TONE_COLOR = {
  warning: "orange.300",
  fresh: "green.300",
  muted: "whiteAlpha.600",
};

/**
 * One honest line about availability, replacing the bare "Open for Work" tag
 * (the actively-looking flag is on ~96% of prod profiles, so it says nothing).
 * Always phrased as a dated declaration, never a promise: declared dates and
 * a "no" render amber, a fresh confirmation renders green with its age.
 *
 * Renders nothing when there is no signal, including when the caller's query
 * did not select the availability fields, so it is safe to drop into any
 * candidate row. `profile` is the candidate's candidateProfile.
 */
// The age renders a step smaller than the statement, so "Free from 12 Oct"
// leads and "2d ago" reads as a footnote to it.
const AGE_FONT_SIZE = { md: "xs", sm: "2xs", xs: "2xs" };

export default function AvailabilityLine({ profile, fontSize = "sm", ...rest }) {
  const label = availabilityLabel(profile);
  if (!label) return null;
  return (
    <Text
      fontSize={fontSize}
      color={TONE_COLOR[label.tone]}
      data-testid="availability-line"
      data-tone={label.tone}
      {...rest}
    >
      {label.text}
      {label.age && " "}
      {label.age && <AvailabilityAge age={label.age} fontSize={AGE_FONT_SIZE[fontSize] ?? "2xs"} />}
    </Text>
  );
}

/** The trailing "2d ago": smaller and dimmer than the statement it qualifies. */
export function AvailabilityAge({ age, fontSize = "2xs" }) {
  return (
    <Text
      as="span"
      fontSize={fontSize}
      opacity={0.7}
      whiteSpace="nowrap"
      data-testid="availability-age"
    >
      {age}
    </Text>
  );
}
